import {
  ArrowUpRight,
  BadgeCheck,
  Check,
  CircleDollarSign,
  Crown,
  ExternalLink,
  Handshake,
  LockKeyhole,
  Megaphone,
  X,
} from "lucide-react";
import { AppSidebarPortal } from "../components/AppShell";
import { DashboardWorkspaceNav } from "../components/DashboardWorkspaceNav";
import { OfferTierBadge } from "../components/ui";
import {
  offerPurchaseLinks,
  paperKingdomAffiliateSignupUrl,
  paperKingdomPartnerResourcesUrl,
} from "../data/offerLinks";
import { usePortal } from "../state/PortalContext";

const resellerPurchase = offerPurchaseLinks["paper-kingdom-reseller"];

const setupSteps = [
  {
    icon: BadgeCheck,
    title: "Request affiliate approval",
    body: "Open the Paper Kingdom affiliate page on WarriorPlus and request approval for the offer.",
  },
  {
    icon: CircleDollarSign,
    title: "Activate your reseller commission",
    body: "Commissions run normally during the first 10 launch days. After that launch window, your approved reseller commission is adjusted to 100%.",
  },
  {
    icon: Megaphone,
    title: "Promote and get paid",
    body: "Collect your unique link and approved campaign material, then send buyers through that tracked link.",
  },
] as const;

export function ResellerRightsDocument() {
  return (
    <div className="reseller-document">
      <section className="reseller-hero" aria-labelledby="reseller-title">
        <div className="reseller-hero__copy">
          <span className="reseller-hero__eyebrow">
            <Crown size={15} aria-hidden="true" /> Paper Kingdom Reseller Rights
          </span>
          <h1 id="reseller-title">Keep 100% of every sale.</h1>
          <p>
            After the 10-day launch window, your commission moves to 100% on
            qualifying Paper Kingdom sales made through your approved WarriorPlus link.
          </p>
          <div className="reseller-hero__actions">
            <a
              className="reseller-action reseller-action--gold"
              href={paperKingdomAffiliateSignupUrl}
              target="_blank"
              rel="noreferrer"
            >
              Request affiliate approval <ArrowUpRight size={17} />
            </a>
            <a
              className="reseller-action reseller-action--ghost"
              href={paperKingdomPartnerResourcesUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open partner resources <ExternalLink size={16} />
            </a>
          </div>
        </div>
        <div className="reseller-hero__summary" aria-label="Reseller commission summary">
          <span className="reseller-hero__crown" aria-hidden="true"><Crown size={31} /></span>
          <div>
            <strong>100%</strong>
            <small>commission after launch</small>
          </div>
          <div>
            <strong>10 days</strong>
            <small>standard launch window</small>
          </div>
        </div>
      </section>

      <section className="reseller-steps" aria-labelledby="reseller-steps-title">
        <header>
          <span>Three simple steps</span>
          <h2 id="reseller-steps-title">Start selling Paper Kingdom</h2>
          <p>Set up the approved link once, then use it for every reseller campaign.</p>
        </header>
        <div className="reseller-steps__grid">
          {setupSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <article key={step.title}>
                <div className="reseller-step__topline">
                  <span>{index + 1}</span>
                  <Icon size={20} aria-hidden="true" />
                </div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="reseller-snapshot" aria-label="Reseller rights summary">
        <div><span>Commission</span><strong>100%</strong><small>After the 10-day launch window</small></div>
        <div><span>Launch window</span><strong>10 days</strong><small>Commissions run normally first</small></div>
        <div><span>Platform</span><strong>WarriorPlus</strong><small>Approval, links, and tracking</small></div>
      </section>

      <section className="reseller-rights-grid" aria-labelledby="rights-details-title">
        <header>
          <span>Licence at a glance</span>
          <h2 id="rights-details-title">Clear rights. Clear boundaries.</h2>
          <p>
            Use the product confidently while protecting the Paper Kingdom brand,
            your customers, and the reseller programme.
          </p>
        </header>
        <div className="reseller-rights-grid__cards">
          <article className="reseller-rights-card reseller-rights-card--allowed">
            <span><Check size={18} /> You may</span>
            <ul>
              <li>Promote Paper Kingdom using your approved WarriorPlus affiliate link.</li>
              <li>Keep the reseller commission shown for qualifying tracked sales.</li>
              <li>Use the supplied reseller campaign materials to support your promotions.</li>
              <li>Create truthful, compliant marketing for your own audience and channels.</li>
            </ul>
          </article>
          <article className="reseller-rights-card reseller-rights-card--restricted">
            <span><X size={18} /> You may not</span>
            <ul>
              <li>Transfer, resell, or grant these reseller rights to another person.</li>
              <li>Private-label Paper Kingdom or remove its ownership notices.</li>
              <li>Redistribute source code, protected libraries, or purchaser data.</li>
              <li>Make guarantees about income, rankings, sales, acceptance, or compliance.</li>
            </ul>
          </article>
        </div>
      </section>

      <p className="reseller-contact">
        Questions? Email us at <strong>mohith@aiagentoffer.com</strong>
      </p>
    </div>
  );
}

export function ResellerRightsLocked() {
  return (
    <main className="reseller-page reseller-page--locked">
      <section className="reseller-lock-card" aria-labelledby="reseller-lock-title">
        <span className="reseller-lock-card__icon"><LockKeyhole size={27} /></span>
        <OfferTierBadge sku="paper-kingdom-reseller" locked />
        <h1 id="reseller-lock-title">Reseller Rights are not enabled</h1>
        <p>
          Unlock the branded Paper Kingdom reseller licence, the affiliate setup
          instructions, and the resources for promoting the product as a reseller.
        </p>
        <a
          className="reseller-lock-card__button"
          href={resellerPurchase.url}
          target="_blank"
          rel="noreferrer"
        >
          {resellerPurchase.label} <ArrowUpRight size={17} />
        </a>
        <small><Handshake size={14} /> One-time OTO 5 upgrade through WarriorPlus</small>
      </section>
    </main>
  );
}

export function ResellerRightsPage() {
  const { entitlements, projects, storageMode } = usePortal();
  const hasResellerRights = entitlements.includes("paper-kingdom-reseller");

  return (
    <>
      <AppSidebarPortal>
        <DashboardWorkspaceNav
          active="reseller"
          projectCount={projects.length}
          cloudWorkspace={storageMode === "cloud"}
          hasResellerRights={hasResellerRights}
        />
      </AppSidebarPortal>
      {hasResellerRights ? (
        <main className="reseller-page"><ResellerRightsDocument /></main>
      ) : (
        <ResellerRightsLocked />
      )}
    </>
  );
}
